import type { ReactNode } from "react";

import { Flex, Paper, ScrollArea } from "@mantine/core";
import clsx from "clsx";
import { ErrorBoundary } from "react-error-boundary";
import { Group, Panel, Separator } from "react-resizable-panels";

import { PanelErrorFallback } from "@/panels/modules/panels/components/PanelErrorFallback";

import styles from "./WorkspaceLayout.module.css";

const HIT_TARGET_MIN_SIZE = { coarse: 8, fine: 4 };

interface WorkspaceLayoutProps {
    leftSidebar: ReactNode;
    leftSidebarOpen: boolean;
    leftSidebarHidden: boolean;
    leftTop?: ReactNode;
    leftTopOpen: boolean;
    leftTopHeight?: string;
    leftBottom: ReactNode;
    rightTop: ReactNode;
    rightTopOpen: boolean;
}

/**
 *     ┌─────────────┬───────────────────────────┬─────────────────────┐
 *     │             │        Left Top           │                     │
 *     │  Left       ├───────────────────────────┤                     │
 *     │  Sidebar    │                           │      Right Top      │
 *     │             │        Left Bottom        │                     │
 *     │             │                           │                     │
 *     └─────────────┴───────────────────────────┴─────────────────────┘
 *
 */
export function WorkspaceLayout({
    leftSidebar,
    leftSidebarOpen,
    leftSidebarHidden,
    leftTop,
    leftTopOpen,
    leftTopHeight = "56px",
    leftBottom,
    rightTop,
    rightTopOpen,
}: WorkspaceLayoutProps) {
    const leftSidebarVisible = leftSidebarOpen && !leftSidebarHidden;
    const leftMainDefaultSize = !leftSidebarOpen ? "50%" : leftSidebarVisible ? "45%" : "60%";
    const rightTopDefaultSize = leftSidebarOpen ? "40%" : "50%";

    return (
        <Group
            id="workspace-redesign-horizontal"
            orientation="horizontal"
            resizeTargetMinimumSize={HIT_TARGET_MIN_SIZE}
        >
            {leftSidebarVisible && (
                <>
                    <Panel id="left-sidebar" defaultSize="240px" minSize="240px" maxSize="240px">
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ScrollArea className={styles.scrollArea}>
                                <Flex pos="absolute" inset={0}>
                                    <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftSidebar}</ErrorBoundary>
                                </Flex>
                            </ScrollArea>
                        </Paper>
                    </Panel>

                    <Separator className={clsx(styles.separator, styles.separatorHorizontal)} />
                </>
            )}

            <Panel id="left-main" defaultSize={leftMainDefaultSize} minSize="400px">
                <Group id="workspace-redesign-left-vertical" orientation="vertical">
                    {leftTopOpen && (
                        <>
                            <Panel
                                id="left-top"
                                defaultSize={leftTopHeight}
                                minSize={leftTopHeight}
                                maxSize={leftTopHeight}
                            >
                                <Paper radius={0} h="100%" className={styles.panelContainer}>
                                    <ScrollArea className={styles.scrollArea}>
                                        <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftTop}</ErrorBoundary>
                                    </ScrollArea>
                                </Paper>
                            </Panel>

                            <Separator className={clsx(styles.separator, styles.separatorVertical)} />
                        </>
                    )}

                    <Panel id="left-bottom" minSize="20px">
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ErrorBoundary fallbackRender={PanelErrorFallback}>{leftBottom}</ErrorBoundary>
                        </Paper>
                    </Panel>
                </Group>
            </Panel>

            {rightTopOpen && (
                <>
                    <Separator className={clsx(styles.separator, styles.separatorHorizontal)} />

                    <Panel id="right-top" defaultSize={rightTopDefaultSize} minSize="440px">
                        <Paper radius={0} h="100%" className={styles.panelContainer}>
                            <ErrorBoundary fallbackRender={PanelErrorFallback}>{rightTop}</ErrorBoundary>
                        </Paper>
                    </Panel>
                </>
            )}
        </Group>
    );
}
